import { defineStore } from "pinia";
import axios from "axios";
import { ActorStore } from "./actorStore";
import { AssetStore } from "./assetStore";
import { CategoriesStore } from "./categoriesStore";
import { useCounterStore } from "./counter";
import { DataEnrichmentStore } from "./dataEnrichmentStore";
import { DirectorStore } from "./directorStore";
import { FavoriteStore } from "./favoriteStore";
import { GenresStore } from "./genresStore";
import { ProfileStore } from "./profileStore";
import { SessionStore } from "./sessionStore";
import { UIStore } from "./uiStore";

type UserAccountState = {
  token: string | null;
  validdate: string | null;
  plan: string;
  active_subscription?: string | null;
  gateway?: string | null;
  accesscontrol?: boolean | string | number;
  is_premium?: boolean | string | number;
  payment_status?: string | null;
  [key: string]: any;
};

type AccountPlan = "free" | "basic" | "premium";

const isTruthy = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["1", "true", "yes"].includes(value.trim().toLowerCase());
  }
  return false;
};

const isFutureValidDate = (value: unknown): boolean => {
  if (value === null || value === undefined || value === "") return false;

  if (typeof value === "number") {
    const milliseconds = value < 10000000000 ? value * 1000 : value;
    return Number.isFinite(milliseconds) && milliseconds > Date.now();
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "0000-00-00" || trimmed === "0000-00-00 00:00:00") {
      return false;
    }

    if (/^\d+$/.test(trimmed)) {
      return isFutureValidDate(Number(trimmed));
    }

    const timestamp = Date.parse(trimmed.replace(" ", "T"));
    return Number.isFinite(timestamp) && timestamp > Date.now();
  }

  return false;
};

const resolveAccountPlan = (account?: Partial<UserAccountState> | null): AccountPlan => {
  const hasActiveAccess =
    isFutureValidDate(account?.validdate) && isTruthy(account?.accesscontrol);

  if (!hasActiveAccess) {
    return "free";
  }

  return isTruthy(account?.is_premium) ? "premium" : "basic";
};

const TOKEN_COOKIE_NAME = "fltk";

const getTokenCookieAttributes = () => {
  const attributes = ["path=/", "SameSite=Lax"];

  if (!import.meta.env.DEV) {
    attributes.push("domain=.flixlatino.com");
  }

  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    attributes.push("Secure");
  }

  return attributes.join(";");
};

const setTokenCookie = (token: string) => {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${TOKEN_COOKIE_NAME}=${encodeURIComponent(
    token
  )};${getTokenCookieAttributes()}`;
};

const clearTokenCookie = () => {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${TOKEN_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;Max-Age=0;${getTokenCookieAttributes()}`;
};

export const UserStore = defineStore("userStore", {
  state: () => ({
    entrypoint: null,
    selectedLanguage: "es",
    token: "",
    flMark: "",
    userAccount: {
      user_id: null,
      token: null,
      validdate: null,
      plan: "",
    } as UserAccountState,
  }),
  actions: {
    clearEntrypoint() {
      this.entrypoint = null;
    },
    setFlMark(flMark: string) {
      this.flMark = flMark;
    },
    setEntrypoint(entrypoint: string) {
      // @ts-ignore
      this.entrypoint = entrypoint === "" ? null : entrypoint;
    },
    logout() {
      const actorStore = ActorStore();
      const assetStore = AssetStore();
      const categoriesStore = CategoriesStore();
      const counterStore = useCounterStore();
      const dataEnrichmentStore = DataEnrichmentStore();
      const directorStore = DirectorStore();
      const favoriteStore = FavoriteStore();
      const genresStore = GenresStore();
      const profileStore = ProfileStore();
      const sessionStore = SessionStore();
      const uiStore = UIStore();

      actorStore.$reset();
      assetStore.$reset();
      categoriesStore.$reset();
      counterStore.count = 0;
      dataEnrichmentStore.$reset();
      directorStore.$reset();
      favoriteStore.$reset();
      genresStore.$reset();
      profileStore.$reset();
      sessionStore.$reset();
      uiStore.$reset();
      this.$reset();

      axios.defaults.params = {};

      if (typeof window !== "undefined") {
        window.localStorage.clear();
        window.sessionStorage.clear();
        window.sessionStorage.setItem("flx:home:force-reload", "1");
      }

      clearTokenCookie();
    },
    setLanguage(language: string | string[]) {
      axios.defaults.params = {};
      axios.defaults.params["lang"] = language;
      this.selectedLanguage = language.toString();
    },

    setAccountStatus(status: string) {
      // @ts-ignore
      this.userAccount.payment_status = status;
    },

    setToken(token: string) {
      this.token = token;
      setTokenCookie(token);
    },
    setAccount(profile: object) {
      this.userAccount = profile as UserAccountState;
    },
    resolveAccountPlan(account?: Partial<UserAccountState> | null): AccountPlan {
      return resolveAccountPlan(account ?? this.userAccount);
    },
  },
  getters: {
    getFlMark(): string {
      return this.flMark;
    },
    getEntrypoint(): string {
      // @ts-ignore
      return this.entrypoint;
    },
    getLanguage(): string {
      return this.selectedLanguage;
    },
    trialEntitlement(): boolean {
      // Treat only subscription-backed trial accounts as trial entitled.
      // Free magic-link accounts can also have active_subscription === "0",
      // but they should not be routed through the paid trial flow.
      return (
        this.userAccount?.active_subscription === "0" &&
        !!this.userAccount?.gateway
      );
    },
    getToken(): string {
      return this.token;
    },
    getAccount(): any {
      return this.userAccount;
    },
    isLoggedIn(): boolean {
      return this.token !== "";
    },
    fullyEntitled(): boolean {
      // @ts-ignore
      return this.userAccount.active_subscription === "1";
    },
    isEntitled(): boolean {
      // @ts-ignore
      return this.token !== "";
    },
    isPaidUser(): boolean {
      return resolveAccountPlan(this.userAccount) !== "free";
    },
    getPlan(): AccountPlan {
      return resolveAccountPlan(this.userAccount);
    },
  },
  persist: {
    enabled: true,
    strategies: [
      {
        key: "flxUser",
        storage: localStorage,
      },
    ],
  },
});
